import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../api";

const MESSENGER_OPTIONS = ["Телеграм", "СМС", "Макс"];

// Показывается по клику на «Сохранить» в сканере — мессенджер/ссылка/категория записи больше не
// вводятся прямо в панели параметров скана (см. ScanParamsPanel.jsx), а подтверждаются здесь, в
// момент фактического сохранения, и только отсюда уходят в пуш в YClients.
export default function SaveDetailsModal({ scanParams, patient, onClose, onSubmit, saving }) {
  const [alias, setAlias] = useState(patient?.alias || patient?.name || "");
  const [messenger, setMessenger] = useState(scanParams.messenger || "");
  const [messengerLink, setMessengerLink] = useState(scanParams.messengerLink || "");
  const [recordLabelIds, setRecordLabelIds] = useState(scanParams.recordLabelIds || []);
  const [recordLabels, setRecordLabels] = useState([]);

  useEffect(() => {
    api.getYclientsRecordLabels().then(r => setRecordLabels(r?.labels || [])).catch(() => {});
  }, []);

  const toggleLabel = (id) => {
    setRecordLabelIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  // Псевдоним — поле пациента: печатается в отчёте вместо имени. Совпадает с именем или пустой —
  // псевдонима нет. Пациент ещё не загрузился — поле не трогаем вовсе (undefined).
  const handleSubmit = () => {
    const value = alias.trim();
    const patientAlias = patient ? (value && value !== patient.name?.trim() ? value : "") : undefined;
    onSubmit({ messenger, messengerLink, recordLabelIds, patientAlias });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[250] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[420px] max-w-[95vw] overflow-hidden flex flex-col" style={{ maxHeight: "85vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">Перед сохранением</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} disabled={saving}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 flex flex-col gap-4">
          {patient && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">ФИО для отчёта (псевдоним)</p>
              <Input
                value={alias}
                onChange={e => setAlias(e.target.value)}
                placeholder={patient.name}
                className="h-8 text-xs"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {alias.trim() && alias.trim() !== patient.name?.trim()
                  ? `В отчёте будет «${alias.trim()}», в программе — «${patient.name}» с псевдонимом рядом`
                  : "По умолчанию в отчёте имя пациента"}
              </p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Мессенджер</p>
            <div className="flex flex-col gap-0.5">
              <div
                className={`flex items-center gap-2 py-1 px-2 rounded-lg cursor-pointer text-xs transition-colors ${!messenger ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                onClick={() => setMessenger("")}
              >
                {!messenger && <span className="text-blue-500">✓</span>}
                <span>— Не указано</span>
              </div>
              {MESSENGER_OPTIONS.map(opt => (
                <div
                  key={opt}
                  className={`flex items-center gap-2 py-1 px-2 rounded-lg cursor-pointer text-xs transition-colors ${messenger === opt ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}
                  onClick={() => setMessenger(opt)}
                >
                  {messenger === opt && <span className="text-blue-500">✓</span>}
                  <span>{opt}</span>
                </div>
              ))}
            </div>
            <Input
              placeholder="Ссылка на мессенджер..."
              value={messengerLink}
              onChange={e => setMessengerLink(e.target.value)}
              className="h-8 text-xs mt-2"
            />
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Категория записи (YClients)</p>
            {recordLabels.length === 0 ? (
              <p className="text-[11px] text-slate-400 py-2">Категории недоступны</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {recordLabels.map(l => {
                  const active = recordLabelIds.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLabel(l.id)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${active ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                    >
                      {l.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 flex-shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Сохранение..." : "Отправить"}
          </Button>
        </div>
      </div>
    </div>
  );
}
